/* eslint-disable */
import { ApiError, ValidationError } from '../api/errors';

/**
 * Extract a user-friendly error message from different error types
 */
export function handleApiError(error: unknown): string {
  // Handle our custom API errors
  if (error instanceof ApiError) {
    // Log for monitoring/debugging
    console.error(`API Error (${error.statusCode}):`, error.message);
    
    if (error instanceof ValidationError) {
      return error.message || 'Please check your input and try again.';
    }
    
    // Generic error with status code
    return error.message || `An error occurred (${error.statusCode})`;
  }
  
  // Handle standard Error objects
  if (error instanceof Error) {
    console.error('Application Error:', error);
    
    // Network errors (offline, timeout, etc)
    if (error.name === 'NetworkError') {
      return 'Unable to connect to the server. Please check your internet connection.';
    }
    
    // Parse errors from fetch responses
    if (error.name === 'SyntaxError' && error.message.includes('JSON')) {
      return 'There was a problem communicating with the server.';
    }
    
    return error.message || 'An unexpected error occurred';
  }
  
  // Handle unexpected error types
  console.error('Unknown Error:', error);
  return 'An unexpected error occurred';
}

/**
 * Format and log errors for consistent handling
 */
export function logError(
  context: string, 
  error: unknown, 
  additionalInfo?: Record<string, any>
): void {
  const timestamp = new Date().toISOString();
  const errorMessage = error instanceof Error ? error.message : String(error);
  const errorStack = error instanceof Error ? error.stack : undefined;
  
  console.error({
    timestamp,
    context,
    message: errorMessage,
    stack: errorStack,
    ...additionalInfo
  });
  
  // Here you could also send to an error monitoring service
  // like Sentry, LogRocket, etc.
}

/**
 * Try-catch wrapper for async functions
 */
export async function tryCatch<T>(
  fn: () => Promise<T>,
  errorHandler?: (error: unknown) => void
): Promise<{ data: T | null; error: Error | null }> {
  try {
    const data = await fn();
    return { data, error: null };
  } catch (err) {
    const error = err instanceof Error ? err : new Error(String(err));
    
    if (errorHandler) {
      errorHandler(error);
    } else {
      console.error(error);
    }
    
    return { data: null, error };
  }
} 